/*
 * Copyright (c) 2023. Collabro Limited
 */
import React, { useRef, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Chip,
  Grow,
  IconButton,
  InputAdornment,
  LinearProgress,
  Slide,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Apartment,
  ArrowCircleUp,
  East,
  MyLocation,
  SwapHoriz,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { baseURL, searchPersonnel } from "../../../api";
import { PersonnelProfile } from "../../PersonnelScreen/PersonnelScreen";
import { AxiosError } from "axios";
import BookingCard from "./BookingCard";
import { DateTime } from "luxon";
import DynamicIcon from "../../Icons/DynamicIcon";
import { Location } from "SearchParameters";

interface BookingPersonnelSearch {
  containerRef: Element | null;
  searchFieldLabel?: string;
  selectedPersonnel: PersonnelProfile | null;
  setSelectedPersonnel: any;
  isReturn: boolean;
  onError: any;
  fromDate: DateTime;
  returnDate: DateTime;
  destination: Location;
}

const BookingPersonnelSearch = (props: BookingPersonnelSearch) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const searchResultRef = useRef<HTMLDivElement>(null);

  const handleValueChange = (e: any) => {
    setSearchQuery(e.target.value);
  };

  const {
    data: personnelSearchResult,
    isFetching: personnelSearchInProgress,
    refetch: sendSearchQuery,
  } = useQuery({
    queryFn: () => searchPersonnel(searchQuery),
    queryKey: ["searchPersonnel"],
    enabled: false,
    refetchOnWindowFocus: false,
    initialData: [],
    cacheTime: 0,
    onSuccess: (data: PersonnelProfile[]) => {
      if (data && data.length === 1) {
        props.setSelectedPersonnel(data[0]);
      } else {
        props.setSelectedPersonnel(null);
      }
    },
    onError: (error: AxiosError) => props.onError(error),
  });

  const handleSearchButtonClicked = async () => {
    await sendSearchQuery();
  };

  return (
    <>
      <Slide
        direction="down"
        in
        unmountOnExit
        mountOnEnter
        container={props.containerRef}
      >
        <TextField
          label={props.searchFieldLabel ?? "Personnel"}
          size="small"
          fullWidth
          variant="outlined"
          autoFocus
          onChange={handleValueChange}
          onKeyDown={async (event) => {
            if (event.key === "Enter") {
              await handleSearchButtonClicked();
            }
          }}
          value={searchQuery}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleSearchButtonClicked} edge="end">
                  <ArrowCircleUp />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Slide>
      {personnelSearchInProgress && <LinearProgress sx={{ mt: 0.1 }} />}
      <div
        style={{
          width: "100%",
          paddingTop: 10,
        }}
        ref={searchResultRef}
      >
        {personnelSearchResult!.length > 1 && (
          <Slide
            direction="down"
            in
            unmountOnExit
            mountOnEnter
            container={searchResultRef.current}
          >
            <Autocomplete
              size="small"
              options={personnelSearchResult}
              getOptionLabel={(option: PersonnelProfile) =>
                `${option.forenames} ${option.surname} (${option.personnelId})`
              }
              value={props.selectedPersonnel}
              onChange={(_, newValue) => {
                props.setSelectedPersonnel(newValue);
              }}
              renderInput={(params) => (
                <TextField autoFocus {...params} label="Multiple Results" />
              )}
            />
          </Slide>
        )}
      </div>
      <div
        style={{
          width: "100%",
          paddingTop: 10,
        }}
      >
        {props.selectedPersonnel && !personnelSearchInProgress && (
          <Grow in>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <BookingCard>
                <Avatar
                  sx={{
                    height: 50,
                    width: 50,
                  }}
                  src={`${baseURL}/personnel/avatar?personnelId=${props.selectedPersonnel.personnelId}`}
                  children={`${props.selectedPersonnel.forenames.charAt(
                    0
                  )}${props.selectedPersonnel.surname.charAt(0)}`}
                />
                <Typography
                  variant="subtitle2"
                  sx={{
                    maxWidth: 110,
                    whiteSpace: "break-spaces",
                    textAlign: "center",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {`${props.selectedPersonnel.forenames} ${props.selectedPersonnel.surname}`}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    maxWidth: 110,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {`ID: ${props.selectedPersonnel.personnelId}`}
                </Typography>
                <Tooltip
                  title={`Employer: ${props.selectedPersonnel.employerName}`}
                  placement="right"
                  arrow
                >
                  <Chip
                    icon={<Apartment />}
                    label={props.selectedPersonnel.employerName}
                    size="small"
                    sx={{
                      maxWidth: 110,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  />
                </Tooltip>
              </BookingCard>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {props.isReturn ? (
                  <>
                    <Typography variant="caption">
                      {props.fromDate.toFormat("dd-LLL")}
                    </Typography>
                    <SwapHoriz fontSize="small" />
                    <Typography variant="caption">
                      {props.returnDate.toFormat("dd-LLL")}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="caption">
                      {props.fromDate.toFormat("dd-LLL")}
                    </Typography>
                    <East fontSize="small" />
                  </>
                )}
              </div>
              <BookingCard>
                <DynamicIcon iconName={props.destination.locationType} />
                <Typography variant="caption">
                  {props.destination.location}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    maxWidth: 110,
                    whiteSpace: "break-spaces",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    textAlign: "center",
                  }}
                >
                  {props.destination.description}
                </Typography>
                <Chip
                  icon={<MyLocation />}
                  label={props.destination.locationType}
                  size="small"
                  sx={{
                    maxWidth: 110,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                />
              </BookingCard>
            </div>
          </Grow>
        )}
      </div>
      {}
    </>
  );
};

export default BookingPersonnelSearch;
